import React, { useCallback, useEffect, useRef, useState } from 'react';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { AttendanceTrackingPageHeader } from 'components/molecules/AttendanceTrackingPageHeader';
import { AttendanceDashboardPanel } from 'components/organisms/AttendanceDashboardPanel';
import { useNavigate } from 'react-router-dom';
import { agent } from 'services/service';
import {
  AttendanceDashboardResponse,
  AttendanceDashboardRow,
  AttendanceDashboardSummary,
  AttendanceLastImport
} from 'services/AttendanceTrackingService';
import {
  AttendanceDashboardPeriod,
  formatDashboardPeriodQuery,
  getLastWeekPeriod
} from 'utils/attendanceDashboardPeriod';
import { formatDashboardSubtitle } from 'utils/formatAttendanceDashboard';
import { formatAttendanceLastImportDate } from 'utils/formatAttendanceLastImport';
import './index.scss';

const PAGE_SIZE = 50;

export const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AttendanceDashboardSummary | null>(
    null
  );
  const [items, setItems] = useState<AttendanceDashboardRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<AttendanceDashboardPeriod>(() =>
    getLastWeekPeriod()
  );
  const [lastImport, setLastImport] = useState<AttendanceLastImport | null>(
    null
  );
  const pageRef = useRef(1);
  const requestIdRef = useRef(0);

  const applyResponse = useCallback(
    (response: AttendanceDashboardResponse, append: boolean) => {
      setSummary(response.summary);
      setTotalCount(response.totalCount);
      setItems((current) =>
        append ? [...current, ...response.items] : response.items
      );
      pageRef.current = response.page;
    },
    []
  );

  const fetchDashboard = useCallback(
    async (page: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      const { from, to } = formatDashboardPeriodQuery(period);

      const response = await agent.AttendanceTrackingService.getDashboard({
        search,
        from,
        to,
        page,
        pageSize: PAGE_SIZE
      });

      if (requestId !== requestIdRef.current) return;

      applyResponse(response.data, append);
    },
    [applyResponse, period, search]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    pageRef.current = 1;

    try {
      await fetchDashboard(1, false);
    } catch {
      setSummary(null);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fetchDashboard]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore) return;
    if (items.length >= totalCount) return;

    setLoadingMore(true);

    try {
      await fetchDashboard(pageRef.current + 1, true);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchDashboard, items.length, loading, loadingMore, totalCount]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => {
        loadInitial();
      },
      search.trim() ? 300 : 0
    );

    return () => window.clearTimeout(timer);
  }, [loadInitial, search]);

  useEffect(() => {
    agent.AttendanceTrackingService.getLastUpdate()
      .then((res) => setLastImport(res.data))
      .catch(() => setLastImport(null));
  }, []);

  const lastUpdatedLabel = lastImport?.lastImportAt
    ? formatAttendanceLastImportDate(lastImport)
    : null;

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handlePeriodChange = (nextPeriod: AttendanceDashboardPeriod) => {
    setPeriod(nextPeriod);
  };

  const hasMore = items.length < totalCount;

  return (
    <div className="attendance-dashboard-page">
      <AttendanceTrackingPageHeader
        title="Attendance Dashboard"
        description={formatDashboardSubtitle(summary ?? undefined)}
        crumbs={[{ label: 'Attendance Tracking' }, { label: 'Dashboard' }]}
        action={
          <div className="attendance-dashboard-page__upload-action">
            <button
              type="button"
              className="attendance-dashboard-page__upload-btn"
              onClick={() => navigate('/attendance-tracking/upload')}
            >
              <span
                className="attendance-dashboard-page__upload-btn-icon"
                aria-hidden
              >
                <UploadFileOutlinedIcon fontSize="small" />
              </span>
              Upload Attendance
            </button>
            {lastUpdatedLabel && (
              <span className="attendance-dashboard-page__last-updated">
                Last updated: {lastUpdatedLabel}
              </span>
            )}
          </div>
        }
      />

      <AttendanceDashboardPanel
        summary={summary}
        items={items}
        totalCount={totalCount}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={loadMore}
        search={search}
        onSearchChange={handleSearchChange}
        period={period}
        onPeriodChange={handlePeriodChange}
      />
    </div>
  );
};
